import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo } from "react";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { getReportTemplateActions } from "../reportTemplateActions";
import { ReportTemplateCallbacks } from "../ReportTemplatesList";
import { ReportTemplateRow } from "../reportTemplatesViewConfig";

export const ReportTemplateActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ReportTemplateRow, string>) => {
        const meta = useViewTableMeta<
            ReportTemplateCallbacks,
            { canManage: boolean }
        >(table);
        const { callbacks, options } = meta;

        if (!callbacks || !options) {
            return null;
        }

        const { apply, menuItems } = getReportTemplateActions({
            template: {
                templateId: row.original.templateId,
                isDefault: row.original.isDefault,
                isArchived: row.original.isArchived,
                canDelete: row.original.canDelete,
            },
            canManage: options.canManage,
        });

        return (
            <ViewTableCell captureClicks>
                <HStack gap={8} align="center" justify="end">
                    <Tooltip content={apply.reason}>
                        <Button
                            variant="primary"
                            size="sm"
                            className={viewTableStyles.showOnHover}
                            disabled={apply.isDisabled}
                            onClick={() => callbacks.onApply(row.original)}
                        >
                            Apply
                        </Button>
                    </Tooltip>
                    <Menu.More
                        className={viewTableStyles.showOnHover}
                        aria-label="Report template actions"
                    >
                        {menuItems.map((item) => (
                            <Menu.Item
                                key={item.key}
                                destructive={item.key === "delete"}
                                disabled={item.isDisabled}
                                disabledReason={item.reason}
                                onClick={() =>
                                    callbacks.onMenuItemSelect({
                                        key: item.key,
                                        template: row.original,
                                    })
                                }
                            >
                                {item.label}
                            </Menu.Item>
                        ))}
                    </Menu.More>
                </HStack>
            </ViewTableCell>
        );
    },
);
